import { DatePicker, Form, message, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";

import style from "../style.module.scss";
import { GlobalRequest } from "../../../../common/services/request";
import { SchedulesService } from "../core/_request";
import { InputSelect } from "../../../../common/components/InputSelect/InputSelect";
import { API_URLs } from "../../../../utilities/API_URLs";
import { FormProvider } from "../../../../common/provider/form/Form.provider";
import { SchedulesInputs } from "../../work_calendar/components/workShedule/schedules/SchedulesInputs";
import { CustomButton } from "../../../../common/components/CustomButton/CustomButton";
import dayjs from "dayjs";
import { Loader } from "../../../../common/components/Loader/Loader";
import { CustomPopConfirm } from "../../../../common/components/PopConfirm/CustomPopConfirm";
import { RiDeleteBin5Line } from "react-icons/ri";
import { CiCircleRemove } from "react-icons/ci";

interface CustomizedSchedulesProps {
  currentType: string;
}

interface Customer {
  "@id": string;
  id: number;
  name: string;
}

interface ScheduleTime {
  "@id"?: string;
  id?: number;
  startTime: string | null;
  endTime: string | null;
  rate: string | null
}

interface ScheduleDay {
  day: {
    "@id": string;
    id: number | string;
    name: string;
  };
  times: ScheduleTime[];
}

interface ScheduleItem {
  "@id"?: string;
  id?: number;

  /**
   * Nom de l'horaire.
   * Ici, on utilise le nom du client.
   */
  name: string;

  customer?: Customer | string;
  customerIri?: string;
  customerName?: string;

  type?: string | { "@id": string };
  typeIri?: string;

  isDraft?: boolean;
  draftKey?: string;
  editorSchedules?: ScheduleDay[];

  cadenceRules?: any[];
  cadenceRulesArray?: any[];
}

const CUSTOMIZED_WORK_SCHEDULE_TYPE_IRI =
  "api/schedule_types/SPECIFIC";

const cloneScheduleDays = (days: ScheduleDay[]): ScheduleDay[] =>
  days.map((item) => ({
    day:
      typeof item.day === "object"
        ? { ...item.day }
        : item.day,
    times: (item.times ?? []).map((time) => ({ ...time })),
  }));

const getResourceId = (
  resource: { id?: number; "@id"?: string } | null | undefined
): number | undefined => {
  if (resource?.id) {
    return resource.id;
  }

  if (!resource?.["@id"]) {
    return undefined;
  }

  const lastPart = resource["@id"].split("/").filter(Boolean).pop();
  const parsedId = Number(lastPart);

  return Number.isNaN(parsedId) ? undefined : parsedId;
};

const getCustomerIri = (
  schedule: ScheduleItem | null
): string | undefined => {
  if (!schedule) return undefined;

  if (schedule.customerIri) {
    return schedule.customerIri;
  }

  if (typeof schedule.customer === "string") {
    return schedule.customer;
  }

  return schedule.customer?.["@id"];
};

const getCustomerName = (
  schedule: ScheduleItem | null
): string => {
  if (!schedule) return "";

  if (schedule.customerName) {
    return schedule.customerName;
  }

  if (
    schedule.customer &&
    typeof schedule.customer === "object"
  ) {
    return schedule.customer.name;
  }

  return schedule.name;
};

const getTypeIri = (
  schedule: ScheduleItem | null
): string | undefined => {
  if (!schedule) return undefined;

  if (schedule.typeIri) {
    return schedule.typeIri;
  }

  if (typeof schedule.type === "string") {
    return schedule.type;
  }

  return schedule.type?.["@id"];
};

const getScheduleKey = (schedule: ScheduleItem): string =>
  schedule.draftKey ??
  schedule["@id"] ??
  String(schedule.id);

/**
 * Transforme la réponse du GET détaillé dans le format attendu
 * par SchedulesInputs :
 *
 * [
 *   {
 *     day: {...},
 *     times: [...]
 *   }
 * ]
 */
const convertScheduleToEditorValue = (
  schedule: any,
  defaultDays: ScheduleDay[]
): ScheduleDay[] => {
  const groupedRules =
    schedule?.cadenceRulesArray ??
    schedule?.scheduleRulesArray;

  /*
   * Cas où l'API renvoie déjà :
   *
   * [
   *   {
   *     day: {...},
   *     times: [...]
   *   }
   * ]
   */
  if (
    Array.isArray(groupedRules) &&
    groupedRules.every((item) => Array.isArray(item?.times))
  ) {
    return cloneScheduleDays(groupedRules);
  }

  /*
   * Cas où l'API renvoie une liste plate :
   *
   * workScheduleRules: [
   *   {
   *     day: {...},
   *     startTime: "10:00",
   *     endTime: "19:00"
   *   }
   * ]
   */
  const flatRules =
    schedule?.cadenceRules ??
    schedule?.rules ??
    [];

  return defaultDays.map((defaultDay) => {
    const defaultDayIri = defaultDay.day?.["@id"];
    const defaultDayId = defaultDay.day?.id;

    const matchingRules = flatRules.filter((rule: any) => {
      const ruleDayIri =
        typeof rule.day === "string"
          ? rule.day
          : rule.day?.["@id"];

      const ruleDayId =
        typeof rule.day === "object"
          ? rule.day?.id
          : undefined;

      return (
        ruleDayIri === defaultDayIri ||
        String(ruleDayId) === String(defaultDayId)
      );
    });

    return {
      day: { ...defaultDay.day },

      /*
       * Pour un horaire existant, on ne remet pas automatiquement
       * 07:00–15:00 sur les jours qui n'ont aucune règle.
       */
      times:
        matchingRules.length > 0
          ? matchingRules.map((rule: any) => ({
              "@id": rule["@id"],
              id: rule.id,
              startTime: rule.startTime,
              endTime: rule.endTime,
              ...(rule.rate ? {rate: (rule.rate).toString()} : {}),
            }))
          : [
              {
                startTime: null,
                endTime: null,
              },
            ],
    };
  });
};

const { RangePicker } = DatePicker;

export const CustomizedCadence = ({ currentType }: CustomizedSchedulesProps) => {
  const [form] = Form.useForm();
  const [validity, setValidity] = useState<{
    validFrom: string | null;
    validTo: string | null;
  }>({
    validFrom: null,
    validTo: null,
  });

  const [selectedCustomer, setSelectedCustomer] =
    useState<Customer | null>(null);

  /**
   * Liste des horaires affichés à gauche.
   */
  const [scheduleList, setScheduleList] = useState<
    ScheduleItem[]
  >([]);

  /**
   * Horaire actuellement affiché à droite.
   */
  const [activeSchedule, setActiveSchedule] =
    useState<ScheduleItem | null>(null);

  /**
   * Valeurs actuellement affichées dans SchedulesInputs.
   */
  const [editorSchedules, setEditorSchedules] = useState<
    ScheduleDay[]
  >([]);

  /**
   * Valeurs par défaut utilisées seulement à la création.
   */
  const [defaultWorkSchedules, setDefaultWorkSchedules] =
    useState<ScheduleDay[]>([]);

  const [isChanged, setIsChanged] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedId, setSelectedId] = useState<any>(null);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);

  useEffect(() => {
    if (currentType !== "main") {
      void Promise.all([
        getWeekLists(),
        getWorkSchedules(),
      ]);
    }
  }, [currentType]);

  const getWeekLists = async () => {
    try {
      const res = await GlobalRequest.get(
        "/api/week_days/select"
      );

      if (res?.status !== HttpStatusCode.Ok) {
        return;
      }

      const days = res.data?.member ?? [];

      const defaultSchedules: ScheduleDay[] = days.map(
        (day: any) => ({
          day,
          times: [
            {
              startTime: "07:00",
              endTime: "15:00",
            },
          ],
        })
      );

      setDefaultWorkSchedules(defaultSchedules);
    } catch (error) {
      console.error(error);
      message.error(
        "Impossible de charger les jours de la semaine."
      );
    }
  };

  /**
   * Charge uniquement la liste affichée à gauche.
   */
  const getWorkSchedules = async (): Promise<
    ScheduleItem[]
  > => {
    try {
      setIsLoading(true);

      const res =
        await SchedulesService.getCadencesList();

      if (res?.status !== HttpStatusCode.Ok) {
        return [];
      }

      /**
       * API Platform renvoie normalement la collection
       * dans member.
       *
       * Adaptez le fallback si votre endpoint utilise
       * un autre nom.
       */
      const schedules: ScheduleItem[] =
        res.data?.member ??
        res.data?.workSchedules ??
        [];

      setScheduleList(schedules);

      return schedules;
    } catch (error) {
      console.error(error);
      message.error(
        "Impossible de charger les horaires."
      );

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Charge le détail d'un horaire lorsque l'utilisateur
   * clique dessus.
   */
  const getWorkSchedule = async (
    schedule: ScheduleItem
  ) => {
    const scheduleId = getResourceId(schedule);

    if (!scheduleId) {
      message.error(
        "L'identifiant de cet horaire est introuvable."
      );
      return;
    }

    try {
      setIsLoading(true);

      const res =
        await SchedulesService.getCadence(
          scheduleId
        );

      if (res?.status !== HttpStatusCode.Ok) {
        return;
      }

      const detailedSchedule: ScheduleItem = {
        ...schedule,
        ...res.data,
        isDraft: false,
      };

      setValidity({
        validFrom: res.data.validFrom,
        validTo: res.data.validTo,
      });

      setActiveSchedule(detailedSchedule);

      setEditorSchedules(
        convertScheduleToEditorValue(
          res.data,
          defaultWorkSchedules
        )
      );

      setIsChanged(false);
    } catch (error) {
      console.error(error);
      message.error(
        "Impossible de charger le détail de cet horaire."
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cette fonction accepte plusieurs formats possibles,
   * selon ce que votre InputSelect renvoie :
   *
   * - l'objet Customer complet ;
   * - { value, label } ;
   * - l'IRI du client avec une option séparée.
   */
  const handleCustomerChange = (
    value: any,
    option?: any
  ) => {
    const source =
      option?.customer ??
      option?.data ??
      option ??
      value;

    const rawValue =
      value?.value ??
      source?.value ??
      value;

    const customerId =
      source?.id ??
      value?.id ??
      (
        typeof rawValue === "number" ||
        /^\d+$/.test(String(rawValue))
          ? Number(rawValue)
          : undefined
      );

    const customerIri =
      source?.["@id"] ??
      value?.["@id"] ??
      (
        typeof rawValue === "string" &&
        rawValue.startsWith("/api/")
          ? rawValue
          : customerId
            ? `/api/customers/${customerId}`
            : undefined
      );

    const customerName =
      source?.name ??
      source?.label ??
      value?.name ??
      option?.label;

    if (!customerIri || !customerName) {
      setSelectedCustomer(null);
      return;
    }

    setSelectedCustomer({
      "@id": customerIri,
      id:
        customerId ??
        Number(
          customerIri.split("/").filter(Boolean).pop()
        ),
      name: customerName,
    });
  };

  /**
   * Appelé lorsque l'utilisateur clique sur Ajouter.
   *
   * Aucun POST n'est fait ici.
   * On crée seulement un brouillon local.
   */
  const handleAdd = () => {
      if (!selectedCustomer) {
        message.warning(
          "Veuillez sélectionner un client."
        );
        return;
      }
  
      if (defaultWorkSchedules.length === 0) {
        message.warning(
          "Les horaires par défaut ne sont pas encore chargés."
        );
        return;
      }
  
      const existingSchedule = scheduleList.find(
        (schedule) =>
          getCustomerIri(schedule) ===
          selectedCustomer["@id"]
      );
  
      if (existingSchedule) {
        if (existingSchedule.isDraft) {
          setActiveSchedule(existingSchedule);
      
          setEditorSchedules(
            cloneScheduleDays(
              existingSchedule.editorSchedules ??
                defaultWorkSchedules
            )
          );
      
          setIsChanged(true);
          setShowInput(false);
      
          return;
        }
      
        message.info(
          "Un horaire existe déjà pour ce client."
        );
      
        void getWorkSchedule(existingSchedule);
        return;
      }
  
      const draftSchedules = cloneScheduleDays(
        defaultWorkSchedules
      );
  
      const draft: ScheduleItem = {
        draftKey: `draft-${selectedCustomer.id}`,
        name: selectedCustomer.name,
  
        customer: selectedCustomer,
        customerIri: selectedCustomer["@id"],
        customerName: selectedCustomer.name,
  
        typeIri: CUSTOMIZED_WORK_SCHEDULE_TYPE_IRI,
  
        isDraft: true,
        editorSchedules: draftSchedules,
      };
  
      setScheduleList((previous) => [
        ...previous.filter((item) => !item.isDraft),
        draft,
      ]);
  
      setActiveSchedule(draft);
      setEditorSchedules(draftSchedules);
  
      /*
       * Même sans modification manuelle, le bouton Enregistrer
       * doit être disponible afin de créer les valeurs par défaut.
       */
      setIsChanged(true);
      setShowInput(false);
    };
  
    const [showInput, setShowInput] = useState<boolean>(false);
  
    const handleShowInput = () => {
    setShowInput(true);
  };
  
    const handleCancelScheduleAddition = () => {
      setSelectedCustomer(null)
      setShowInput(false)
    }

  const handleSelectSchedule = (
    schedule: ScheduleItem
  ) => {
    if (schedule.isDraft) {
      setActiveSchedule(schedule);

      setEditorSchedules(
        cloneScheduleDays(
          schedule.editorSchedules ??
            defaultWorkSchedules
        )
      );

      setIsChanged(true);
      return;
    }

    /*
     * Pour un horaire existant, on récupère son détail
     * avec getWorkShedule(id).
     */
    void getWorkSchedule(schedule);
  };

  const handleEditorChange = (
    schedules: ScheduleDay[]
  ) => {
    setEditorSchedules(schedules);

    /*
     * On conserve les modifications du brouillon même si
     * l'utilisateur clique momentanément sur un autre élément.
     */
    if (activeSchedule?.isDraft) {
      const activeKey =
        getScheduleKey(activeSchedule);

      setActiveSchedule((previous) =>
        previous
          ? {
              ...previous,
              editorSchedules: schedules,
            }
          : previous
      );

      setScheduleList((previous) =>
        previous.map((schedule) =>
          getScheduleKey(schedule) === activeKey
            ? {
                ...schedule,
                editorSchedules: schedules,
              }
            : schedule
        )
      );
    }
  };

  const createRulesPayload = () =>
    editorSchedules.flatMap((day) =>
      (day.times ?? [])
        /*
         * Une ligne entièrement vide n'est pas envoyée.
         */
        .filter(
          (time) =>
            Boolean(time.startTime) &&
            Boolean(time.endTime)
        )
        .map((time) => {
          const rule: {
            "@id"?: string;
            day: string;
            startTime: string;
            endTime: string;
            rate?: string;
          } = {
            day: day.day["@id"],
            startTime: time.startTime as string,
            endTime: time.endTime as string,
            ...(time.rate ? {rate: (time.rate)?.toString() as string} : {}),
          };

          /*
           * L'@id est conservé uniquement pour les règles
           * déjà présentes dans la base.
           */
          if (time["@id"]) {
            rule["@id"] = time["@id"];
          }

          return rule;
        })
    );

  const handleSubmit = async () => {
    if (!activeSchedule) {
      return;
    }

    const hasIncompleteTime = editorSchedules.some(
      (day) =>
        day.times.some((time) => {
          const hasStart = Boolean(time.startTime);
          const hasEnd = Boolean(time.endTime);

          return hasStart !== hasEnd;
        })
    );

    if (hasIncompleteTime) {
      message.warning(
        "Certaines plages horaires sont incomplètes."
      );
      return;
    }

    const customerIri =
      getCustomerIri(activeSchedule);

    const typeIri =
      getTypeIri(activeSchedule) ??
      CUSTOMIZED_WORK_SCHEDULE_TYPE_IRI;

    if (!customerIri) {
      message.error(
        "Le client associé est introuvable."
      );
      return;
    }

    if (!typeIri) {
      message.error(
        "Le type d'horaire est introuvable."
      );
      return;
    }

    const cadenceRules =
      createRulesPayload();

    try {
      setIsLoadingSubmit(true);

      if (activeSchedule.isDraft) {
        const dataToPost = {
          name:
            activeSchedule.name ||
            getCustomerName(activeSchedule),

          customer: customerIri,
          type: typeIri,
          validFrom: validity.validFrom
        ? dayjs(validity.validFrom, "DD/MM/YYYY").format("DD/MM/YYYY")
        : null,

        validTo: validity.validTo
        ? dayjs(validity.validTo, "DD/MM/YYYY").format("DD/MM/YYYY")
        : null,
          cadenceRules,
        };

        const res =
          await SchedulesService.postCadence(
            dataToPost
          );

        const isCreated =
          res?.status === HttpStatusCode.Created ||
          res?.status === HttpStatusCode.Ok;

        if (!isCreated) {
          return;
        }

        message.success(
          "L'horaire a été créé."
        );

        const draftKey =
          activeSchedule.draftKey;

        /*
         * On enlève le brouillon avant de recharger
         * la liste officielle.
         */
        setScheduleList((previous) =>
          previous.filter(
            (schedule) =>
              schedule.draftKey !== draftKey
          )
        );

        const refreshedSchedules =
          await getWorkSchedules();

        const createdId =
          getResourceId(res.data);

        if (createdId) {
          const createdSchedule =
            refreshedSchedules.find(
              (schedule) =>
                getResourceId(schedule) === createdId
            ) ?? res.data;

          await getWorkSchedule(createdSchedule);
        } else {
          setActiveSchedule(null);
          setEditorSchedules([]);
        }

        setIsChanged(false);
        setSelectedCustomer(null);
        return;
      }

      const scheduleId =
        getResourceId(activeSchedule);

      if (!scheduleId) {
        message.error(
          "L'identifiant de l'horaire est introuvable."
        );
        return;
      }

      const dataToPatch = {
        id: scheduleId,
        name: activeSchedule.name,
        customer: customerIri,
        type: typeIri,
        validFrom: validity.validFrom
        ? dayjs(validity.validFrom, "DD/MM/YYYY").format("DD/MM/YYYY")
        : null,

        validTo: validity.validTo
        ? dayjs(validity.validTo, "DD/MM/YYYY").format("DD/MM/YYYY")
        : null,
        cadenceRules,
      };

      const res =
        await SchedulesService.patchCadence(
          dataToPatch
        );

      const isUpdated =
        res?.status === HttpStatusCode.Ok ||
        res?.status === HttpStatusCode.NoContent;

      if (!isUpdated) {
        return;
      }

      message.success(
        "L'horaire a été modifié."
      );

      setIsChanged(false);

      await getWorkSchedules();
      await getWorkSchedule(activeSchedule);
    } catch (error) {
      console.error(error);

      message.error(
        activeSchedule.isDraft
          ? "Impossible de créer l'horaire."
          : "Impossible de modifier l'horaire."
      );
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const handleClosePopConfirm = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsOpenConfirm(false);
    setSelectedId(null);
  };

  const handleClickOnDeleteCustomer = (
      e: React.MouseEvent,
      schedule: ScheduleItem
    ) => {
      e.stopPropagation();
    
      // Draft => suppression immédiate
      if (schedule.isDraft) {
        setScheduleList((previous) =>
          previous.filter(
            (item) => getScheduleKey(item) !== getScheduleKey(schedule)
          )
        );
    
        if (
          activeSchedule &&
          getScheduleKey(activeSchedule) === getScheduleKey(schedule)
        ) {
          setActiveSchedule(null);
          setEditorSchedules([]);
          setSelectedCustomer(null);
          setIsChanged(false);
        }
    
        return;
      }
    
      // Horaire enregistré => PopConfirm
      const scheduleId = getResourceId(schedule);
    
      if (!scheduleId) {
        message.error("L'identifiant de cette cadence est introuvable.");
        return;
      }
    
      setSelectedId(scheduleId);
      setIsOpenConfirm(true);
    };

  const handleConfirmDeleteCustomer = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedId === null) return;

    try {
      const res =
        await SchedulesService.deleteCadence(selectedId);

      const isDeleted =
        res?.status === HttpStatusCode.Ok ||
        res?.status === HttpStatusCode.NoContent;

      if (!isDeleted) {
        message.error(
          "Impossible de supprimer la cadence."
        );
        return;
      }

      setScheduleList((previous) =>
        previous.filter(
          (item) => getResourceId(item) !== selectedId
        )
      );

      if (getResourceId(activeSchedule) === selectedId) {
        setActiveSchedule(null);
        setEditorSchedules([]);
      }

      message.success("La cadence a été supprimée.");
    } catch (error) {
      console.error(error);
      message.error(
        "Impossible de supprimer la cadence."
      );
    } finally {
      handleClosePopConfirm();
    }
  };

  return (
    <><Loader isLoading={isLoading} />
    <div
      className={
        style.customizedSchedules__containers
      }
    >
      <div
        className={
          style.customizedSchedules__primary_container
        }
      >

        <div
          className={
            style.customizedSchedules__clients_list
          }
        >
          {scheduleList.map((schedule) => {
            const scheduleKey =
              getScheduleKey(schedule);

            const isActive =
              activeSchedule &&
              getScheduleKey(activeSchedule) ===
                scheduleKey;

                return (
                  <div
                    key={scheduleKey}
                    onClick={() => handleSelectSchedule(schedule)}
                    className={`${style.customizedSchedules__client_item} ${
                      isActive ? style['customizedSchedules__client_item--active'] : ''
                    }`}
                    aria-pressed={Boolean(isActive)}
                  >
                    <span className={style.customizedSchedules__client_name}>
                      {getCustomerName(schedule)}
                    </span>

                    <CustomPopConfirm
                      title="Voulez-vous vraiment supprimer cette cadence ?"
                      okText="Oui"
                      cancelText="Non"
                      isOpen={
                        isOpenConfirm &&
                        selectedId === getResourceId(schedule)
                      }
                      onClose={handleClosePopConfirm}
                      onConfirm={handleConfirmDeleteCustomer}
                    >
                      <div
                        className={`${style.customizedSchedules__customer_delete} ${
                          isOpenConfirm && selectedId === getResourceId(schedule)
                            ? style['customizedSchedules__customer_delete--active']
                            : ''
                        }`}
                        onClick={(e) => handleClickOnDeleteCustomer(e, schedule)}
                      >
                        <RiDeleteBin5Line
                          title="Supprimer"
                          className={style.customizedSchedules__customer_delete_icon}
                        />
                      </div>
                    </CustomPopConfirm>
                  </div>
                );
          })}

          {!isLoading &&
            scheduleList.length === 0 && (
              <div
                className={
                  style.customizedSchedules__empty_list
                }
              >
                Aucune cadence personnalisée.
              </div>
            )}
        </div>
        {!showInput ? (
          <>
          {(activeSchedule?.isDraft) ? (
            <span className={style.customizedSchedules__indication}>Cliquez sur « Enregister » pour sauvegarder les modifications.</span>
          ) : (
            <button
              className={
                style.customizedSchedules__add_btn
              }
              type="button"
              onClick={handleShowInput}
            >
              Ajouter une cadence personnalisé
            </button>
          )}
        </>
        ) : (
          <div
          className={
            style.customizedSchedules__customer_top_section
          }
        >
          <CiCircleRemove title="Annuler" size={25} style={{marginTop: "2.5px", color: "red", cursor: "pointer"}} onClick={handleCancelScheduleAddition}/>
          <div
            className={
              style.customizedSchedules__search_input
            }
          >
            <InputSelect
              value={
                selectedCustomer
                  ? {
                      label: selectedCustomer.name,
                      value: selectedCustomer["@id"],
                    }
                  : undefined
              }
              placeholder="Sélectionner un client"
              onChange={handleCustomerChange}
              uri={
                API_URLs.settings.customers
                  .getCustomerSelect
              }
              excludedValue={
                scheduleList
                  .map((schedule) => getCustomerIri(schedule))
                  .filter((customerIri): customerIri is string => Boolean(customerIri))
              }
              style={{ width: "100%" }}
            />
          </div>

          <button
            className={
              style.customizedSchedules__add_btn
            }
            type="button"
            disabled={
              !selectedCustomer ||
              defaultWorkSchedules.length === 0
            }
            onClick={handleAdd}
          >
            Continuer
          </button>
        </div>
        )}
      </div>

      <div
        className={
          style.customizedSchedules__secondary_container
        }
      >
        {isLoading && activeSchedule && (
          <Skeleton active />
        )}

        {!isLoading && activeSchedule && (
          <FormProvider
            Form={Form}
            form={form}
            debug
            onFinish={handleSubmit}
            onFinishFailed={(error) => {
              console.error(error);
            }}
          >
            <div className={style.schedulesInputs}>
        <span className={style.schedulesInputs__label}>
            Période de validation :
        </span>
        <RangePicker
            className={style.schedulesInputs__picker}
            format="DD/MM/YYYY"
            value={[
            validity.validFrom ? dayjs(validity.validFrom, "DD/MM/YYYY") : null,
            validity.validTo ? dayjs(validity.validTo, "DD/MM/YYYY") : null,
            ]}
            onChange={(dates) => {
            setValidity({
                validFrom: dates?.[0]?.format("DD/MM/YYYY") ?? null,
                validTo: dates?.[1]?.format("DD/MM/YYYY") ?? null,
            });
            setIsChanged(true);
            }}
        />
        </div>

            <SchedulesInputs
              value={editorSchedules}
              onChange={handleEditorChange}
              onChanged={() =>
                setIsChanged(true)
              }
              rate
            />
           {!(validity.validFrom || validity.validTo) && <p style={{color: "red"}}>Veuillez renseigner la période de validation.</p>}

            <div className="mt-4">
              <CustomButton
                type="submit"
                variant="primary"
                disabled={((!isChanged) || !(validity.validFrom || validity.validTo))}
                isLoading={isLoadingSubmit}
              >
                Enregistrer
              </CustomButton>
            </div>
          </FormProvider>
        )}
      </div>
    </div>
    </>
  );
};